/**
 * TanStack Query hooks for the PencilOut backend. Centralised here so
 * pages stay thin and we have one place to look when an endpoint
 * shape changes.
 */

import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { IS_CONTRACTOR_APP, STAFF_CONSOLE_ORIGIN } from "@/appTarget";

import { useOptionalClerk } from "@/lib/clerk";
import { ApiError, apiBase, apiClient } from "./client";
import type {
  AdminAuditEventResponse,
  AppConfigResponse,
  ApplyTemplateToQuoteRequest,
  ApplyTemplateToQuoteResponse,
  BillingEventResponse,
  ChangeOrderResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  ClientResponse,
  ClientSummaryResponse,
  ConsumeMagicLinkResponse,
  CreateChangeOrderRequest,
  CreateClientRequest,
  CreateJobRequest,
  CreateJobTemplateRequest,
  CreateOrgRequest,
  CreatePaymentRequest,
  CreatePricingRuleRequest,
  CreateServiceTokenRequest,
  CreateServiceTokenResponse,
  CreateSubcontractorRequest,
  CreateWebhookEndpointRequest,
  CurrentUserResponse,
  DashboardMetrics,
  DashboardRangeKey,
  EmailEventResponse,
  GenerateInvoiceResponse,
  ImpersonateResponse,
  InboxItem,
  InviteUserRequest,
  JobAttachmentResponse,
  JobDetail,
  JobSummary,
  JobTemplateResponse,
  MagicLinkConsumeBody,
  MagicLinkRequestBody,
  MintShareLinkResponse,
  ModelCatalogResponse,
  OnboardingState,
  OrganizationDetail,
  OrganizationSummary,
  OverageDetailResponse,
  PhoneStartBody,
  PhoneVerifyBody,
  PlanVersionResponse,
  PricingDriftCheckResponse,
  PricingProposalResponse,
  PricingRuleResponse,
  PricingVersionRecordResponse,
  ProjectPaymentResponse,
  PublicPlanTierResponse,
  PublicQuoteResponse,
  QuoteCommentResponse,
  QuoteResponse,
  RequestPlanChangeBody,
  RequestPlanChangeResponse,
  SaveNewPlanVersionRequest,
  SaveNewPlanVersionResponse,
  SendQuoteRequest,
  SendQuoteResponse,
  ServiceTokenResponse,
  SignupRequest,
  SignupResponse,
  SubcontractorResponse,
  SubscriptionStatusResponse,
  SuggestionsResponse,
  SyncStripeResponse,
  UpdateAppConfigRequest,
  UpdateChangeOrderRequest,
  UpdateClientRequest,
  UpdateJobRequest,
  UpdateJobTemplateRequest,
  UpdateOrgBrandingRequest,
  UpdateOrgRequest,
  UpdatePaymentRequest,
  UpdatePricingRuleRequest,
  UpdateQuoteRequest,
  UpdateSubcontractorRequest,
  UpdateUserRequest,
  UpdateWebhookEndpointRequest,
  UsageEventsResponse,
  UsageSummaryResponse,
  UserResponse,
  WalkthroughResponse,
  WebhookDeliveryResponse,
  WebhookEndpointResponse,
  WebhookEndpointWithSecret,
} from "./types";

/** Optional filter set passed to ``/usage/*`` endpoints. */
export interface UsageQueryParams {
  organization_id?: string;
  start?: string; // YYYY-MM-DD
  end?: string; // YYYY-MM-DD
  job_id?: string;
  quote_id?: string;
  walkthrough_id?: string;
  operation?: string;
  limit?: number;
  offset?: number;
}

/** Query-key registry. Centralized so cache invalidation stays accurate. */
/** Parse a non-OK ``fetch`` response (used by the multipart upload
 *  paths that bypass ``apiClient``) into the same :class:`ApiError`
 *  shape JSON callers throw, so call sites can use a single
 *  ``instanceof ApiError`` branch. Falls back to ``statusText`` if the
 *  body isn't valid JSON. */
async function readMultipartError(res: Response): Promise<ApiError> {
  let detail = res.statusText;
  let errorCode: string | null = null;
  try {
    const data = (await res.json()) as { detail?: unknown };
    const raw = data?.detail;
    if (typeof raw === "string") {
      detail = raw;
    } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      if (typeof obj.message === "string") detail = obj.message;
      else if (typeof obj.reason === "string") detail = obj.reason;
      if (typeof obj.error === "string") errorCode = obj.error;
    }
  } catch {
    /* keep statusText */
  }
  return new ApiError(res.status, detail, errorCode);
}

export const queryKeys = {
  me: ["me"] as const,
  users: ["users"] as const,
  usersForOrg: (orgId: string) => ["users", { orgId }] as const,
  user: (id: string) => ["users", id] as const,
  jobs: ["jobs"] as const,
  jobsForOrg: (orgId: string) => ["jobs", { orgId }] as const,
  jobsForClient: (clientId: string) => ["jobs", { clientId }] as const,
  job: (id: string) => ["jobs", id] as const,
  jobAttachments: (jobId: string) => ["jobs", jobId, "attachments"] as const,
  walkthroughs: ["walkthroughs"] as const,
  walkthroughsForJob: (jobId: string) => ["walkthroughs", { jobId }] as const,
  walkthrough: (id: string) => ["walkthroughs", id] as const,
  organizations: ["organizations"] as const,
  organization: (id: string) => ["organizations", id] as const,
  pricingRules: ["pricing-rules"] as const,
  pricingRulesForOrg: (orgId: string) => ["pricing-rules", { orgId }] as const,
  pricingRule: (id: string) => ["pricing-rules", id] as const,
  quotes: ["quotes"] as const,
  quotesForWalkthrough: (id: string) => ["quotes", { walkthroughId: id }] as const,
  quotesForJob: (id: string) => ["quotes", { jobId: id }] as const,
  quote: (id: string) => ["quotes", id] as const,
  quoteCustomerPreview: (id: string) => ["quotes", id, "customer-preview"] as const,
  quoteComments: (quoteId: string) => ["quotes", quoteId, "comments"] as const,
  changeOrders: (quoteId: string) => ["quotes", quoteId, "change-orders"] as const,
  payments: (quoteId: string) => ["quotes", quoteId, "payments"] as const,
  inbox: ["quote-comments", "inbox"] as const,
  usageSummary: (params: UsageQueryParams) => ["usage", "summary", params] as const,
  usageEvents: (params: UsageQueryParams) => ["usage", "events", params] as const,
  appConfig: ["admin", "config"] as const,
  planVersionCurrent: ["admin", "plans", "current"] as const,
  planVersions: ["admin", "plans", "versions"] as const,
  publicPlans: ["plans", "public"] as const,
  dashboard: ["dashboard"] as const,
  billingEventsForOrg: (orgId: string) => ["billing", "events", { orgId }] as const,
  subscriptionStatusForOrg: (orgId: string) =>
    ["billing", "subscription-status", { orgId }] as const,
  overageDetailForOrg: (orgId: string) => ["billing", "overage-detail", { orgId }] as const,
  serviceTokens: ["admin", "service-tokens"] as const,
  pricingProposals: (onlyPending: boolean) =>
    ["admin", "pricing", "proposals", { onlyPending }] as const,
  pricingVersions: ["admin", "pricing", "versions"] as const,
  clients: ["clients"] as const,
  subcontractors: ["subcontractors"] as const,
  subcontractor: (id: string) => ["subcontractors", id] as const,
  clientsForOrg: (orgId: string) => ["clients", { orgId }] as const,
  client: (id: string) => ["clients", id] as const,
  jobTemplates: ["job-templates"] as const,
  jobTemplatesForOrg: (orgId: string) => ["job-templates", { orgId }] as const,
  jobTemplate: (id: string) => ["job-templates", id] as const,
  webhookEndpoints: ["webhook-endpoints"] as const,
  webhookEndpoint: (id: string) => ["webhook-endpoints", id] as const,
  webhookDeliveries: (endpointId: string) =>
    ["webhook-endpoints", endpointId, "deliveries"] as const,
  onboardingState: ["onboarding", "state"] as const,
};

/** GET /onboarding/state — checklist for the new-contractor
 *  post-signup orientation card. Refetches in the background on
 *  every Dashboard mount so completing a step elsewhere
 *  (e.g., adding a client) reflects on next load. */
export function useOnboardingState() {
  return useQuery<OnboardingState | null, ApiError>({
    queryKey: queryKeys.onboardingState,
    queryFn: () => apiClient.get<OnboardingState>("/onboarding/state").catch(() => null),
    // Don't aggressively refetch — the dashboard mounts often
    // and the state changes infrequently.
    staleTime: 60_000,
  });
}

// ---- Auth ----

export function useCurrentUser(): UseQueryResult<CurrentUserResponse | null, ApiError> {
  return useQuery<CurrentUserResponse | null, ApiError>({
    queryKey: queryKeys.me,
    // Treat 401 as "not logged in" (return null) instead of an error
    // — this lets the gate render a login screen without a flash.
    queryFn: async () => {
      try {
        return await apiClient.get<CurrentUserResponse>("/auth/me");
      } catch (err) {
        if (err instanceof Error && "status" in err && (err as ApiError).status === 401) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

export function useRequestMagicLink() {
  return useMutation<void, ApiError, MagicLinkRequestBody>({
    mutationFn: async (body) => {
      await apiClient.post("/auth/magic-link/request", body);
    },
  });
}

/** Kick off an SMS OTP for ``phone_number``. Server normalizes to
 *  E.164. Returns 204 unconditionally to avoid leaking which numbers
 *  are registered. */
export function useRequestPhoneOTP() {
  return useMutation<void, ApiError, PhoneStartBody>({
    mutationFn: async (body) => {
      await apiClient.post("/auth/phone/start", body);
    },
  });
}

/** Submit the user-typed 6-digit code. On success, sets the session
 *  cookie + returns the user payload (mirrors useConsumeMagicLink). */
export function useVerifyPhoneOTP() {
  const qc = useQueryClient();
  return useMutation<ConsumeMagicLinkResponse, ApiError, PhoneVerifyBody>({
    mutationFn: (body) => apiClient.post<ConsumeMagicLinkResponse>("/auth/phone/verify", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp.user);
    },
  });
}

export function useSignup() {
  return useMutation<SignupResponse, ApiError, SignupRequest>({
    mutationFn: (body) => apiClient.post<SignupResponse>("/signup", body),
  });
}

/** PATCH /organizations/{id}/branding — set tagline / default terms /
 *  deposit pct. Org_admin scope; org_admins only land on their own
 *  org. The optimistic update strategy here is "let the response
 *  win" — the BrandingCard surfaces a "Saved" badge that auto-fades
 *  after success; no need to local-update before the round-trip. */
export function useUpdateOrgBranding(orgId: string) {
  const qc = useQueryClient();
  return useMutation<OrganizationDetail, ApiError, UpdateOrgBrandingRequest>({
    mutationFn: (body) =>
      apiClient.patch<OrganizationDetail>(`/organizations/${orgId}/branding`, body),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.organization(orgId), fresh);
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
}

/** Multipart upload of an org's logo. PUT /organizations/{id}/logo. */
export function useUploadOrgLogo(orgId: string) {
  const qc = useQueryClient();
  return useMutation<OrganizationSummary, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${apiBase}/organizations/${orgId}/logo`, {
        method: "PUT",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload failed: ${res.status} ${txt}`);
      }
      return (await res.json()) as OrganizationSummary;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
      // Also drop the per-org detail cache — the BrandingCard /
      // JobsPage callout reads has_logo from there and want the
      // freshly-uploaded logo to render right after the PUT lands.
      void qc.invalidateQueries({ queryKey: queryKeys.organization(orgId) });
    },
  });
}

export function useConsumeMagicLink() {
  const qc = useQueryClient();
  return useMutation<ConsumeMagicLinkResponse, ApiError, MagicLinkConsumeBody>({
    mutationFn: (body) =>
      apiClient.post<ConsumeMagicLinkResponse>("/auth/magic-link/consume", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp.user);
    },
  });
}

export interface EmailCompleteSignupBody {
  verified_email_token: string;
  display_name: string;
  company_name: string;
  terms_accepted_version: string;
}

/** POST /auth/email/complete-signup — finishes inline signup that
 *  started with an unknown-email magic-link request. Server-side
 *  validates the HMAC-signed token, creates the User + Org, returns
 *  a session. UX-symmetric to ``useCompletePhoneSignup`` (the phone
 *  path's verified-token → account-creation flow). */
export function useCompleteEmailSignup() {
  const qc = useQueryClient();
  return useMutation<ConsumeMagicLinkResponse, ApiError, EmailCompleteSignupBody>({
    mutationFn: (body) =>
      apiClient.post<ConsumeMagicLinkResponse>("/auth/email/complete-signup", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const clerk = useOptionalClerk();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      // Kill both halves of auth: the legacy server-side session (and
      // its cookie) plus the Clerk session — otherwise the bearer
      // token keeps /auth/me green and "log out" silently no-ops.
      await apiClient.post("/auth/logout");
      await clerk?.signOut();
    },
    onSuccess: () => {
      qc.setQueryData(queryKeys.me, null);
      // Drop everything else — caches belonged to the prior session.
      qc.clear();
    },
  });
}

// ---- Users ----

export function useUsers(orgId?: string | null): UseQueryResult<UserResponse[], ApiError> {
  return useQuery<UserResponse[], ApiError>({
    queryKey: orgId ? queryKeys.usersForOrg(orgId) : queryKeys.users,
    queryFn: () =>
      apiClient.get<UserResponse[]>(orgId ? `/users?organization_id=${orgId}` : "/users"),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation<UserResponse, ApiError, InviteUserRequest>({
    mutationFn: (body) => apiClient.post<UserResponse>("/users", body),
    // Same partial-success rationale as useCreateOrganization.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation<UserResponse, ApiError, UpdateUserRequest>({
    mutationFn: (body) => apiClient.patch<UserResponse>(`/users/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users });
      void qc.invalidateQueries({ queryKey: queryKeys.user(id) });
    },
  });
}

/** DELETE /users/{id} — pencilout_admin only. Hard delete; cascades
 *  through sessions / magic links / etc. The endpoint refuses
 *  (409) when the user authored Jobs / JobTemplates / WebhookEndpoints
 *  (RESTRICT FKs); admin must clean those up or delete the whole org
 *  to cascade everything. Refuses self-delete (422). */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
}

// ---- Self-service profile (PATCH /me + email/phone change) ----

export interface UpdateMeRequest {
  display_name?: string;
}

/** PATCH /me — update the signed-in user's display name. Email +
 *  phone changes go through the verified flows below. */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation<CurrentUserResponse, ApiError, UpdateMeRequest>({
    mutationFn: (body) => apiClient.patch<CurrentUserResponse>("/me", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp);
    },
  });
}

export interface RequestEmailChangeBody {
  new_email: string;
}

/** Send a confirmation link to the new email address. The user must
 *  click the link in their NEW inbox to confirm; nothing changes on
 *  our side until then. Returns 204 even on collision (enumeration-
 *  resistant) — UI should say "we sent a link if the address is
 *  available." */
export function useRequestEmailChange() {
  return useMutation<void, ApiError, RequestEmailChangeBody>({
    mutationFn: async (body) => {
      await apiClient.post("/me/email/request-change", body);
    },
  });
}

export interface ConfirmEmailChangeBody {
  token: string;
}

/** Consume the token from the confirmation link (delivered to the new
 *  email). User must be signed in for this to work — the token + the
 *  active session together prove ownership. */
export function useConfirmEmailChange() {
  const qc = useQueryClient();
  return useMutation<CurrentUserResponse, ApiError, ConfirmEmailChangeBody>({
    mutationFn: (body) => apiClient.post<CurrentUserResponse>("/me/email/confirm-change", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp);
    },
  });
}

export interface RequestPhoneChangeBody {
  new_phone: string;
}

/** Trigger Twilio Verify to send an OTP to the new phone. Same
 *  enumeration-resistance as email — silent 204 on collision. */
export function useRequestPhoneChange() {
  return useMutation<void, ApiError, RequestPhoneChangeBody>({
    mutationFn: async (body) => {
      await apiClient.post("/me/phone/request-change", body);
    },
  });
}

export interface VerifyPhoneChangeBody {
  new_phone: string;
  code: string;
}

/** Verify the OTP, swap the phone, mark verified. Server re-checks
 *  uniqueness at this step in case someone grabbed the number
 *  between request and verify. */
export function useVerifyPhoneChange() {
  const qc = useQueryClient();
  return useMutation<CurrentUserResponse, ApiError, VerifyPhoneChangeBody>({
    mutationFn: (body) => apiClient.post<CurrentUserResponse>("/me/phone/verify-change", body),
    onSuccess: (resp) => {
      qc.setQueryData(queryKeys.me, resp);
    },
  });
}

// ---- Organizations ----

export function useOrganizations(): UseQueryResult<OrganizationSummary[], ApiError> {
  return useQuery<OrganizationSummary[], ApiError>({
    queryKey: queryKeys.organizations,
    queryFn: () => apiClient.get<OrganizationSummary[]>("/organizations"),
  });
}

export function useOrganization(
  id: string | undefined,
): UseQueryResult<OrganizationDetail, ApiError> {
  return useQuery<OrganizationDetail, ApiError>({
    queryKey: id ? queryKeys.organization(id) : ["organizations", "disabled"],
    queryFn: () => apiClient.get<OrganizationDetail>(`/organizations/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation<OrganizationDetail, ApiError, CreateOrgRequest>({
    mutationFn: (body) => apiClient.post<OrganizationDetail>("/organizations", body),
    // onSettled (not onSuccess) so a partial success — org+user created
    // but the invitation email failed (502 from the API) — still refreshes
    // the orgs list. The row IS in the DB; the cache just needs to know.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
    },
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation<OrganizationDetail, ApiError, UpdateOrgRequest>({
    mutationFn: (body) => apiClient.patch<OrganizationDetail>(`/organizations/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
      void qc.invalidateQueries({ queryKey: queryKeys.organization(id) });
    },
  });
}

export interface SetCompedRequest {
  comped: boolean;
  /** Tier to switch to when un-comping. Defaults server-side to "free". */
  revert_to?: string;
  /** Optional human-readable note for the audit log. */
  reason?: string;
}

/** PATCH /organizations/{id}/comped — pencilout_admin only. Flips the
 *  org's plan_tier to "comped" (or back to ``revert_to`` / "free").
 *  Comped is a Veridon-internal pilot tier: bypasses quota, never
 *  appears as an upgrade or purchase option in any user-facing UI. */
export function useSetOrgComped(id: string) {
  const qc = useQueryClient();
  return useMutation<OrganizationDetail, ApiError, SetCompedRequest>({
    mutationFn: (body) => apiClient.patch<OrganizationDetail>(`/organizations/${id}/comped`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
      void qc.invalidateQueries({ queryKey: queryKeys.organization(id) });
      // The contractor's PlanBillingPage view-state depends on the new
      // plan_tier — invalidate their subscription-status cache so the
      // CurrentPlanCard / CompedPlanCard swap takes effect on next
      // render without making the contractor refresh.
      void qc.invalidateQueries({
        queryKey: ["billing", "subscription-status"],
      });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/organizations/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.organizations });
      // Jobs list shows org names — drop it so cross-org admins don't
      // see stale rows for deleted orgs.
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

// ---- Jobs ----

/** GET /jobs with optional org + archive scoping.
 *
 *  ``view``:
 *  - ``"active"`` (default): excludes archived jobs
 *  - ``"all"``: includes archived
 *  - ``"archived"``: only archived
 *
 *  Cache key includes ``view`` so the active and archive views don't
 *  clobber each other when the user toggles. */
export interface JobsListFilters {
  /** Repeat-OR statuses. Empty = no constraint. */
  statuses?: string[];
  /** ``true``: jobs with at least one walkthrough.
   *  ``false``: jobs without one.
   *  ``undefined``: no constraint. */
  hasWalkthrough?: boolean;
}

export function useJobs(
  orgId?: string | null,
  view: "active" | "all" | "archived" = "active",
  filters: JobsListFilters = {},
): UseQueryResult<JobSummary[], ApiError> {
  // Stable query-key fingerprint so toggling filters doesn't keep
  // stale data around. Sort statuses for cache-key stability.
  const filterKey = {
    statuses: [...(filters.statuses ?? [])].sort(),
    has_walkthrough: filters.hasWalkthrough ?? null,
  };
  return useQuery<JobSummary[], ApiError>({
    queryKey: orgId
      ? [...queryKeys.jobsForOrg(orgId), { view, filterKey }]
      : [...queryKeys.jobs, { view, filterKey }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (orgId) params.set("organization_id", orgId);
      if (view === "all") params.set("include_archived", "true");
      else if (view === "archived") params.set("archived_only", "true");
      for (const s of filters.statuses ?? []) params.append("status", s);
      if (filters.hasWalkthrough !== undefined) {
        params.set("has_walkthrough", String(filters.hasWalkthrough));
      }
      const qs = params.toString();
      return apiClient.get<JobSummary[]>(`/jobs${qs ? `?${qs}` : ""}`);
    },
  });
}

/** POST /jobs/{id}/archive — flips status to archived so it drops
 *  out of the default Jobs list. */
export function useArchiveJob() {
  const qc = useQueryClient();
  return useMutation<JobDetail, ApiError, string>({
    mutationFn: (id) => apiClient.post<JobDetail>(`/jobs/${id}/archive`),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
      qc.setQueryData(queryKeys.job(job.id), job);
    },
  });
}

/** POST /jobs/{id}/unarchive — flips status back to open and the
 *  job re-appears in the default list. */
export function useUnarchiveJob() {
  const qc = useQueryClient();
  return useMutation<JobDetail, ApiError, string>({
    mutationFn: (id) => apiClient.post<JobDetail>(`/jobs/${id}/unarchive`),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
      qc.setQueryData(queryKeys.job(job.id), job);
    },
  });
}

export function useJobsForClient(
  clientId: string | undefined,
): UseQueryResult<JobSummary[], ApiError> {
  return useQuery<JobSummary[], ApiError>({
    queryKey: clientId ? queryKeys.jobsForClient(clientId) : ["jobs", "disabled"],
    queryFn: () => apiClient.get<JobSummary[]>(`/jobs?client_id=${clientId}`),
    enabled: Boolean(clientId),
  });
}

/** GET /quotes — list quotes scoped to the caller's org (or all
 *  orgs for pencilout_admin). Mirrors the iOS QuotesListView's
 *  data hook so both surfaces consume the same shape. The server
 *  returns the rich :class:`QuoteResponse`; the list page renders
 *  a subset (status, total, sent date) and links each row to
 *  ``/quotes/:id`` for the full detail. */
export function useQuotesList(statuses?: string[]): UseQueryResult<QuoteResponse[], ApiError> {
  const filterKey = [...(statuses ?? [])].sort();
  return useQuery<QuoteResponse[], ApiError>({
    queryKey: [...queryKeys.quotes, { filterKey }],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const s of statuses ?? []) params.append("status", s);
      const qs = params.toString();
      return apiClient.get<QuoteResponse[]>(qs ? `/quotes?${qs}` : "/quotes");
    },
  });
}

export function useJob(id: string | undefined): UseQueryResult<JobDetail, ApiError> {
  return useQuery<JobDetail, ApiError>({
    queryKey: id ? queryKeys.job(id) : ["jobs", "disabled"],
    queryFn: () => apiClient.get<JobDetail>(`/jobs/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation<JobDetail, ApiError, CreateJobRequest>({
    mutationFn: (body) => apiClient.post<JobDetail>("/jobs", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

export function useUpdateJob(id: string) {
  const qc = useQueryClient();
  return useMutation<JobDetail, ApiError, UpdateJobRequest>({
    mutationFn: (body) => apiClient.patch<JobDetail>(`/jobs/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
      void qc.invalidateQueries({ queryKey: queryKeys.job(id) });
    },
  });
}

// ---- Clients ----

export function useClients(orgId?: string | null): UseQueryResult<ClientResponse[], ApiError> {
  return useQuery<ClientResponse[], ApiError>({
    queryKey: orgId ? queryKeys.clientsForOrg(orgId) : queryKeys.clients,
    queryFn: () =>
      apiClient.get<ClientResponse[]>(orgId ? `/clients?organization_id=${orgId}` : "/clients"),
  });
}

export function useClient(id: string | undefined): UseQueryResult<ClientResponse, ApiError> {
  return useQuery<ClientResponse, ApiError>({
    queryKey: id ? queryKeys.client(id) : ["clients", "disabled"],
    queryFn: () => apiClient.get<ClientResponse>(`/clients/${id}`),
    enabled: Boolean(id),
  });
}

/** Quote rollup + lifetime value for a client's detail page.
 *  Backed by `GET /clients/{id}/summary`. Cached separately
 *  from `useClient` so the basic profile renders instantly
 *  while the heavier aggregation streams in. */
export function useClientSummary(
  id: string | undefined,
): UseQueryResult<ClientSummaryResponse, ApiError> {
  return useQuery<ClientSummaryResponse, ApiError>({
    queryKey: id ? ["clients", id, "summary"] : ["clients", "summary", "disabled"],
    queryFn: () => apiClient.get<ClientSummaryResponse>(`/clients/${id}/summary`),
    enabled: Boolean(id),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<ClientResponse, ApiError, CreateClientRequest>({
    mutationFn: (body) => apiClient.post<ClientResponse>("/clients", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clients });
    },
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation<ClientResponse, ApiError, UpdateClientRequest>({
    mutationFn: (body) => apiClient.patch<ClientResponse>(`/clients/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clients });
      void qc.invalidateQueries({ queryKey: queryKeys.client(id) });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/clients/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clients });
      // Job rows show client_name; refetch so deleted-client jobs go grey.
      void qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

// ---- Subcontractors ----

export function useSubcontractors(
  orgId?: string | null,
  trade?: string | null,
): UseQueryResult<SubcontractorResponse[], ApiError> {
  return useQuery<SubcontractorResponse[], ApiError>({
    queryKey: orgId
      ? [...queryKeys.subcontractors, { orgId, trade }]
      : [...queryKeys.subcontractors, { trade }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (orgId) params.set("organization_id", orgId);
      if (trade) params.set("trade", trade);
      const qs = params.toString();
      return apiClient.get<SubcontractorResponse[]>(`/subcontractors${qs ? `?${qs}` : ""}`);
    },
  });
}

export function useSubcontractor(
  id: string | undefined,
): UseQueryResult<SubcontractorResponse, ApiError> {
  return useQuery<SubcontractorResponse, ApiError>({
    queryKey: id ? queryKeys.subcontractor(id) : ["subcontractors", "disabled"],
    queryFn: () => apiClient.get<SubcontractorResponse>(`/subcontractors/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateSubcontractor() {
  const qc = useQueryClient();
  return useMutation<SubcontractorResponse, ApiError, CreateSubcontractorRequest>({
    mutationFn: (body) => apiClient.post<SubcontractorResponse>("/subcontractors", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.subcontractors });
    },
  });
}

export function useUpdateSubcontractor(id: string) {
  const qc = useQueryClient();
  return useMutation<SubcontractorResponse, ApiError, UpdateSubcontractorRequest>({
    mutationFn: (body) => apiClient.patch<SubcontractorResponse>(`/subcontractors/${id}`, body),
    onSuccess: (sub) => {
      qc.setQueryData(queryKeys.subcontractor(id), sub);
      void qc.invalidateQueries({ queryKey: queryKeys.subcontractors });
    },
  });
}

export function useDeleteSubcontractor() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/subcontractors/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.subcontractors });
    },
  });
}

// ---- Pricing rules ----

export function usePricingRules(
  orgId?: string | null,
  includeArchived = false,
): UseQueryResult<PricingRuleResponse[], ApiError> {
  return useQuery<PricingRuleResponse[], ApiError>({
    queryKey: orgId
      ? [...queryKeys.pricingRulesForOrg(orgId), { includeArchived }]
      : [...queryKeys.pricingRules, { includeArchived }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (orgId) params.set("organization_id", orgId);
      if (includeArchived) params.set("include_archived", "true");
      const qs = params.toString();
      return apiClient.get<PricingRuleResponse[]>(qs ? `/pricing-rules?${qs}` : "/pricing-rules");
    },
  });
}

export function useCreatePricingRule() {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, ApiError, CreatePricingRuleRequest>({
    mutationFn: (body) => apiClient.post<PricingRuleResponse>("/pricing-rules", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
    },
  });
}

export function useUpdatePricingRule(id: string) {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, ApiError, UpdatePricingRuleRequest>({
    mutationFn: (body) => apiClient.patch<PricingRuleResponse>(`/pricing-rules/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRule(id) });
    },
  });
}

export function useDeletePricingRule() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/pricing-rules/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
    },
  });
}

// Additive: insert any seed (category, tier) combos that aren't yet
// on the org. Existing rules are left untouched — markup tweaks,
// custom baselines, edited categories all survive. Idempotent.
export function useSeedMissingPricingRuleDefaults() {
  const qc = useQueryClient();
  return useMutation<
    { already_present: number; added: number },
    ApiError,
    { organizationId?: string }
  >({
    mutationFn: ({ organizationId }) => {
      const path = organizationId
        ? `/pricing-rules/seed-missing-defaults?organization_id=${organizationId}`
        : "/pricing-rules/seed-missing-defaults";
      return apiClient.post<{ already_present: number; added: number }>(path, {});
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
    },
  });
}

// Destructive: wipes every pricing rule for the org and re-seeds the
// Charlotte defaults. Use only when the contractor wants a clean
// slate; otherwise prefer ``useSeedMissingPricingRuleDefaults``.
export function useResetPricingRulesToDefaults() {
  const qc = useQueryClient();
  return useMutation<{ deleted: number; seeded: number }, ApiError, { organizationId?: string }>({
    mutationFn: ({ organizationId }) => {
      const path = organizationId
        ? `/pricing-rules/reset-to-defaults?organization_id=${organizationId}`
        : "/pricing-rules/reset-to-defaults";
      return apiClient.post<{ deleted: number; seeded: number }>(path, {});
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
    },
  });
}

// ---- Pricing-rule learning loop ----
// Surfaces baseline suggestions derived from contractor edits to
// AI-generated line items. The list endpoint silently omits rules
// without enough observations; the apply / dismiss endpoints
// respond per-rule.

export interface LearningSuggestion {
  rule_id: string;
  category: string;
  tier: string;
  unit: string;
  current_baseline_cents: number;
  suggested_baseline_cents: number;
  delta_cents: number;
  observation_count: number;
  raw_observation_count: number;
  accepted_observation_count: number;
  oldest_observation_at: string;
  newest_observation_at: string;
}

export interface LearningSummary {
  organization_id: string;
  suggestions: LearningSuggestion[];
}

export function useLearningSuggestions(orgId: string | null) {
  return useQuery({
    queryKey: orgId ? ["pricing-rules-learning", { orgId }] : ["pricing-rules-learning"],
    queryFn: () => {
      const qs = orgId ? `?organization_id=${orgId}` : "";
      return apiClient.get<LearningSummary>(`/pricing-rules/learning${qs}`);
    },
  });
}

export function useApplyLearnedBaseline() {
  const qc = useQueryClient();
  return useMutation<
    {
      new_rule_id: string;
      superseded_rule_id: string;
      new_baseline_cents: number;
      observations_consumed: number;
    },
    ApiError,
    string
  >({
    mutationFn: (ruleId) => apiClient.post(`/pricing-rules/${ruleId}/apply-learned-baseline`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pricingRules });
      void qc.invalidateQueries({ queryKey: ["pricing-rules-learning"] });
    },
  });
}

export function useDismissLearnedObservations() {
  const qc = useQueryClient();
  return useMutation<{ observations_dismissed: number }, ApiError, string>({
    mutationFn: (ruleId) =>
      apiClient.post(`/pricing-rules/${ruleId}/dismiss-learned-observations`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pricing-rules-learning"] });
    },
  });
}

// ---- Quotes ----

export function useQuotesForWalkthrough(
  walkthroughId: string | undefined,
): UseQueryResult<QuoteResponse[], ApiError> {
  return useQuery<QuoteResponse[], ApiError>({
    queryKey: walkthroughId
      ? queryKeys.quotesForWalkthrough(walkthroughId)
      : ["quotes", "disabled"],
    queryFn: () => apiClient.get<QuoteResponse[]>(`/quotes?walkthrough_id=${walkthroughId}`),
    enabled: Boolean(walkthroughId),
  });
}

export function useQuote(id: string | undefined): UseQueryResult<QuoteResponse, ApiError> {
  return useQuery<QuoteResponse, ApiError>({
    queryKey: id ? queryKeys.quote(id) : ["quotes", "disabled"],
    queryFn: () => apiClient.get<QuoteResponse>(`/quotes/${id}`),
    enabled: Boolean(id),
    // Poll while the contractor's looking at the quote so customer-
    // side actions (accept/decline/view/sign) reflect within 30s
    // without a manual refresh. Tab-focus refetch is on by default,
    // so switching away and back also picks up changes immediately.
    refetchInterval: 30_000,
  });
}

/** GET /quotes/{id}/customer-preview — authed, side-effect-free
 *  preview of the homeowner-facing payload. Does NOT increment
 *  ``view_count`` or set ``client_viewed_at``. Used by the
 *  contractor's "Preview customer view" button so checking the
 *  rendering doesn't pollute the genuine view-tracking signal.
 *  Returns the same `PublicQuoteResponse` shape the public route
 *  ships at `/q/{token}` so the preview page can reuse the
 *  customer-facing visual blocks verbatim. */
export function useQuoteCustomerPreview(
  id: string | undefined,
): UseQueryResult<PublicQuoteResponse, ApiError> {
  return useQuery<PublicQuoteResponse, ApiError>({
    queryKey: id ? queryKeys.quoteCustomerPreview(id) : ["quotes", "preview-disabled"],
    queryFn: () => apiClient.get<PublicQuoteResponse>(`/quotes/${id}/customer-preview`),
    enabled: Boolean(id),
  });
}

export function useGenerateQuoteFromWalkthrough() {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, string>({
    mutationFn: (walkthroughId) =>
      apiClient.post<QuoteResponse>("/quotes/generate", {
        walkthrough_id: walkthroughId,
      }),
    onSuccess: (quote) => {
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      if (quote.walkthrough_id) {
        void qc.invalidateQueries({
          queryKey: queryKeys.quotesForWalkthrough(quote.walkthrough_id),
        });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

/**
 * Generate ONE quote from N walkthroughs — the "combine into a quote"
 * flow. The server merges scope across the source walkthroughs (with
 * AI dedup of overlapping work) and writes a `QuoteWalkthroughSource`
 * row per id, ordered by the array's position. Composes with the
 * quota-shift change: a combined quote is ONE quota event regardless
 * of source count.
 *
 * Caller passes the walkthrough ids in the desired primary→secondary
 * order. Validates server-side: every walkthrough must be processed
 * AND belong to the same job.
 */
export function useGenerateQuoteFromWalkthroughs() {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, { walkthroughIds: string[]; jobId?: string }>({
    mutationFn: ({ walkthroughIds, jobId }) =>
      apiClient.post<QuoteResponse>("/quotes/generate", {
        walkthrough_ids: walkthroughIds,
        ...(jobId ? { job_id: jobId } : {}),
      }),
    onSuccess: (quote) => {
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

export function useGenerateQuoteFromText() {
  const qc = useQueryClient();
  return useMutation<
    QuoteResponse,
    ApiError,
    { jobId: string; scopeText: string; transcript?: string }
  >({
    mutationFn: ({ jobId, scopeText, transcript }) =>
      apiClient.post<QuoteResponse>("/quotes/generate", {
        job_id: jobId,
        scope_text: scopeText,
        transcript,
      }),
    onSuccess: (quote) => {
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

export function useCreateBlankQuote() {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, string>({
    mutationFn: (jobId) => apiClient.post<QuoteResponse>("/quotes", { job_id: jobId }),
    onSuccess: (quote) => {
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

export function useQuotesForJob(
  jobId: string | undefined,
): UseQueryResult<QuoteResponse[], ApiError> {
  return useQuery<QuoteResponse[], ApiError>({
    queryKey: jobId ? queryKeys.quotesForJob(jobId) : ["quotes", "disabled"],
    queryFn: () => apiClient.get<QuoteResponse[]>(`/quotes?job_id=${jobId}`),
    enabled: Boolean(jobId),
  });
}

export function useUpdateQuote(id: string) {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, UpdateQuoteRequest>({
    mutationFn: (body) => apiClient.patch<QuoteResponse>(`/quotes/${id}`, body),
    onSuccess: (quote) => {
      qc.setQueryData(queryKeys.quote(id), quote);
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      if (quote.walkthrough_id) {
        void qc.invalidateQueries({
          queryKey: queryKeys.quotesForWalkthrough(quote.walkthrough_id),
        });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

export function useSuggestQuoteAdditions(quoteId: string) {
  return useMutation<SuggestionsResponse, ApiError, void>({
    mutationFn: () => apiClient.post<SuggestionsResponse>(`/quotes/${quoteId}/suggest-additions`),
  });
}

export function useQuoteComments(quoteId: string) {
  return useQuery<QuoteCommentResponse[], ApiError>({
    queryKey: queryKeys.quoteComments(quoteId),
    queryFn: () => apiClient.get<QuoteCommentResponse[]>(`/quotes/${quoteId}/comments`),
    enabled: Boolean(quoteId),
    refetchInterval: 30_000,
  });
}

/** Post a contractor comment on a quote.
 *
 *  Supports two paths:
 *  - Text-only → JSON POST (legacy + cheaper). The default when
 *    ``attachments`` is missing or empty.
 *  - Text + files → ``multipart/form-data`` with ``body`` (text),
 *    ``parent_comment_id`` (optional UUID), and zero-or-more
 *    ``attachments`` parts. Backend caps at 10 attachments per
 *    comment and rejects bad MIME / oversize files with
 *    ``400 invalid_attachment``. */
export function usePostQuoteComment(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<
    QuoteCommentResponse,
    ApiError,
    {
      body: string;
      parent_comment_id?: string | null;
      attachments?: File[];
    }
  >({
    mutationFn: async (input) => {
      const files = input.attachments ?? [];
      if (files.length === 0) {
        return apiClient.post<QuoteCommentResponse>(`/quotes/${quoteId}/comments`, {
          body: input.body,
          parent_comment_id: input.parent_comment_id ?? null,
        });
      }
      const form = new FormData();
      form.append("body", input.body);
      if (input.parent_comment_id) {
        form.append("parent_comment_id", input.parent_comment_id);
      }
      for (const file of files) {
        form.append("attachments", file, file.name);
      }
      const res = await fetch(`${apiBase}/quotes/${quoteId}/comments`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw await readMultipartError(res);
      return (await res.json()) as QuoteCommentResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quoteComments(quoteId) });
      // Posting a contractor reply usually clears this quote from
      // the inbox — refetch so the badge updates.
      void qc.invalidateQueries({ queryKey: queryKeys.inbox });
    },
  });
}

export function useInbox(): UseQueryResult<InboxItem[], ApiError> {
  return useQuery<InboxItem[], ApiError>({
    queryKey: queryKeys.inbox,
    queryFn: () => apiClient.get<InboxItem[]>("/quote-comments/inbox"),
    refetchInterval: 30_000,
  });
}

// ---- Attachments (job CRUD + comment promote) ----

/** GET /jobs/{jobId}/attachments — curated files hung off a job
 *  (direct uploads + promoted-from-comment). Order: ``created_at`` asc. */
export function useJobAttachments(jobId: string) {
  return useQuery<JobAttachmentResponse[], ApiError>({
    queryKey: queryKeys.jobAttachments(jobId),
    queryFn: () => apiClient.get<JobAttachmentResponse[]>(`/jobs/${jobId}/attachments`),
    enabled: Boolean(jobId),
  });
}

/** POST /jobs/{jobId}/attachments — multipart, label is required.
 *  Allowed MIMEs (image/jpeg, image/png, image/heic, image/webp,
 *  application/pdf) and size caps (10 MB images, 20 MB PDFs) enforced
 *  server-side; clients should pre-validate to avoid round-trips. */
export function useUploadJobAttachment(jobId: string) {
  const qc = useQueryClient();
  return useMutation<JobAttachmentResponse, ApiError, { file: File; label: string }>({
    mutationFn: async ({ file, label }) => {
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("label", label);
      const res = await fetch(`${apiBase}/jobs/${jobId}/attachments`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw await readMultipartError(res);
      return (await res.json()) as JobAttachmentResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobAttachments(jobId) });
    },
  });
}

/** PATCH /jobs/{jobId}/attachments/{aid} — rename. ``org_admin``-gated
 *  server-side. Body: ``{ label: string }``. */
export function useRenameJobAttachment(jobId: string) {
  const qc = useQueryClient();
  return useMutation<JobAttachmentResponse, ApiError, { attachmentId: string; label: string }>({
    mutationFn: ({ attachmentId, label }) =>
      apiClient.patch<JobAttachmentResponse>(`/jobs/${jobId}/attachments/${attachmentId}`, {
        label,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobAttachments(jobId) });
    },
  });
}

/** DELETE /jobs/{jobId}/attachments/{aid}. ``org_admin``-gated
 *  server-side. */
export function useDeleteJobAttachment(jobId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (attachmentId) => {
      await apiClient.delete(`/jobs/${jobId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobAttachments(jobId) });
    },
  });
}

/** POST /quotes/{quoteId}/comments/{commentId}/attachments/{aid}/promote-to-job
 *  — copies a comment-attachment onto the parent job as a curated
 *  ``JobAttachment``. Body: ``{ label: string }``. The new attachment's
 *  source is ``"promoted_from_comment"``. Source comment-attachment
 *  is left in place; deleting it later nulls
 *  ``promoted_from_comment_attachment_id`` on the curated copy but the
 *  file + label survive. */
export function usePromoteCommentAttachmentToJob(quoteId: string, jobId: string) {
  const qc = useQueryClient();
  return useMutation<
    JobAttachmentResponse,
    ApiError,
    { commentId: string; attachmentId: string; label: string }
  >({
    mutationFn: ({ commentId, attachmentId, label }) =>
      apiClient.post<JobAttachmentResponse>(
        `/quotes/${quoteId}/comments/${commentId}/attachments/${attachmentId}/promote-to-job`,
        { label },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobAttachments(jobId) });
    },
  });
}

export function useSendQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<SendQuoteResponse, ApiError, SendQuoteRequest>({
    mutationFn: (body) => apiClient.post<SendQuoteResponse>(`/quotes/${quoteId}/send`, body),
    onSuccess: ({ quote }) => {
      qc.setQueryData(queryKeys.quote(quoteId), quote);
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

/** Mint a fresh share link without sending any email/SMS — used by
 *  the iMessage handoff flow. The contractor clicks "Send via Text"
 *  on the quote editor; the SPA hits this endpoint, gets a
 *  ``share_url`` + a ``suggested_message`` body, then opens
 *  ``sms:phone?body=...`` so the contractor sends the text from
 *  their own phone. PencilOut doesn't send any SMS — zero
 *  per-message cost, zero A2P 10DLC registration friction. */
export function useMintShareLink(quoteId: string) {
  return useMutation<MintShareLinkResponse, ApiError, void>({
    mutationFn: () => apiClient.post<MintShareLinkResponse>(`/quotes/${quoteId}/share-link`, {}),
  });
}

/** Mark an off-platform deposit as received on an accepted
 *  quote. PencilOut doesn't process the payment — contractor
 *  collects via Stripe / Square / check / ACH and confirms
 *  here so the homeowner portal + pipeline view stay accurate.
 *  422 when the quote is in draft / declined / expired state. */
export function useMarkDepositReceived(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, { amount_cents: number; note: string | null }>({
    mutationFn: (body) =>
      apiClient.post<QuoteResponse>(`/quotes/${quoteId}/deposit-received`, body),
    onSuccess: (quote) => {
      qc.setQueryData(queryKeys.quote(quoteId), quote);
      void qc.invalidateQueries({
        queryKey: queryKeys.quotesForJob(quote.job_id),
      });
    },
  });
}

/** Manually trigger a reminder email on a sent-but-unanswered
 *  quote. Server mints a fresh share token + bumps
 *  ``last_reminder_sent_at`` so the auto-cron's cadence clock
 *  resets. Doesn't count against the org's reminder cap.
 *  422 when the quote isn't in ``sent`` state or has no
 *  client email. */
export function useSendQuoteReminderNow(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<QuoteResponse, ApiError, void>({
    mutationFn: () => apiClient.post<QuoteResponse>(`/quotes/${quoteId}/send-reminder`),
    onSuccess: (quote) => {
      qc.setQueryData(queryKeys.quote(quoteId), quote);
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
      void qc.invalidateQueries({ queryKey: queryKeys.quotesForJob(quote.job_id) });
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/quotes/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
    },
  });
}

// ---- Walkthroughs ----

export function useWalkthroughs(): UseQueryResult<WalkthroughResponse[], ApiError> {
  return useQuery<WalkthroughResponse[], ApiError>({
    queryKey: queryKeys.walkthroughs,
    queryFn: () => apiClient.get<WalkthroughResponse[]>("/walkthroughs"),
  });
}

/** Walkthroughs filtered by status — powers admin triage views.
 *  ``status="failed"`` for the Failed Walkthroughs page. */
export function useWalkthroughsByStatus(
  status: string,
  limit = 200,
): UseQueryResult<WalkthroughResponse[], ApiError> {
  return useQuery<WalkthroughResponse[], ApiError>({
    queryKey: ["walkthroughs", { status, limit }],
    queryFn: () =>
      apiClient.get<WalkthroughResponse[]>(
        `/walkthroughs?status=${encodeURIComponent(status)}&limit=${limit}`,
      ),
    staleTime: 5_000,
  });
}

export function useWalkthroughsForJob(
  jobId: string | undefined,
): UseQueryResult<WalkthroughResponse[], ApiError> {
  return useQuery<WalkthroughResponse[], ApiError>({
    queryKey: jobId ? queryKeys.walkthroughsForJob(jobId) : ["walkthroughs", "disabled"],
    queryFn: () => apiClient.get<WalkthroughResponse[]>(`/walkthroughs?job_id=${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: 5_000,
  });
}

export function useWalkthrough(
  id: string | undefined,
): UseQueryResult<WalkthroughResponse, ApiError> {
  return useQuery<WalkthroughResponse, ApiError>({
    queryKey: id ? queryKeys.walkthrough(id) : ["walkthroughs", "disabled"],
    queryFn: () => apiClient.get<WalkthroughResponse>(`/walkthroughs/${id}`),
    enabled: Boolean(id),
    refetchInterval: 5_000,
  });
}

export function useRetriggerProcessing(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, void>({
    mutationFn: () => apiClient.post<WalkthroughResponse>(`/walkthroughs/${walkthroughId}/process`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.walkthrough(walkthroughId) });
      void qc.invalidateQueries({ queryKey: queryKeys.walkthroughs });
    },
  });
}

/** Upload a walkthrough video for a job. Bypasses the JSON apiClient
 * because uploads are multipart/form-data — the backend reads
 * `job_id` (Form) + `file` (UploadFile). */
export function useUploadWalkthrough(jobId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("job_id", jobId);
      form.append("file", file);
      const res = await fetch(`${apiBase}/walkthroughs`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`Upload failed: ${res.status} ${detail}`);
      }
      return (await res.json()) as WalkthroughResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.walkthroughs });
      void qc.invalidateQueries({
        queryKey: queryKeys.walkthroughsForJob(jobId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.job(jobId) });
    },
  });
}

/** Create an empty (collecting) walkthrough for a job. Returns the row
 *  so callers can immediately start uploading clips into it. */
export function useCreateWalkthrough(jobId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, void>({
    mutationFn: () =>
      apiClient.post<WalkthroughResponse>("/walkthroughs", {
        job_id: jobId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.walkthroughs });
      void qc.invalidateQueries({
        queryKey: queryKeys.walkthroughsForJob(jobId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.job(jobId) });
    },
  });
}

/** Append a clip to a collecting walkthrough. Multipart upload —
 *  bypass the JSON apiClient. */
export function useAddClip(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${apiBase}/walkthroughs/${walkthroughId}/clips`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`Clip upload failed: ${res.status} ${detail}`);
      }
      return (await res.json()) as WalkthroughResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.walkthrough(walkthroughId),
      });
    },
  });
}

/** Remove a clip from a collecting walkthrough. */
export function useDeleteClip(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, string>({
    mutationFn: (clipId) =>
      apiClient.delete<WalkthroughResponse>(`/walkthroughs/${walkthroughId}/clips/${clipId}`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.walkthrough(walkthroughId),
      });
    },
  });
}

/** Finalize the walkthrough — fires the AI pipeline + quota gate.
 *  Each finalize call is a chargeable event. */
export function useFinalizeWalkthrough(walkthroughId: string) {
  const qc = useQueryClient();
  return useMutation<WalkthroughResponse, ApiError, void>({
    mutationFn: () =>
      apiClient.post<WalkthroughResponse>(`/walkthroughs/${walkthroughId}/finalize`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.walkthrough(walkthroughId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.walkthroughs });
    },
  });
}

// ---- Usage / billing ----

function buildUsageQS(params: UsageQueryParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function useUsageSummary(
  params: UsageQueryParams = {},
): UseQueryResult<UsageSummaryResponse, ApiError> {
  return useQuery<UsageSummaryResponse, ApiError>({
    queryKey: queryKeys.usageSummary(params),
    queryFn: () => apiClient.get<UsageSummaryResponse>(`/usage/summary${buildUsageQS(params)}`),
    staleTime: 30_000,
  });
}

export function useUsageEvents(
  params: UsageQueryParams = {},
): UseQueryResult<UsageEventsResponse, ApiError> {
  return useQuery<UsageEventsResponse, ApiError>({
    queryKey: queryKeys.usageEvents(params),
    queryFn: () => apiClient.get<UsageEventsResponse>(`/usage/events${buildUsageQS(params)}`),
    staleTime: 30_000,
  });
}

// ---- Impersonation (PencilOut admin only) ----

export function useImpersonateUser() {
  const qc = useQueryClient();
  return useMutation<ImpersonateResponse, ApiError, string>({
    mutationFn: (userId) => apiClient.post<ImpersonateResponse>(`/users/${userId}/impersonate`),
    onSuccess: () => {
      // /auth/me now returns the target user (with impersonated_by
      // populated). Drop everything else from cache so the next
      // navigation re-fetches under the new identity.
      qc.invalidateQueries();
    },
  });
}

export function useStopImpersonating() {
  const qc = useQueryClient();
  return useMutation<
    { user: CurrentUserResponse; session_token: string; expires_at: string },
    ApiError,
    void
  >({
    mutationFn: () =>
      apiClient.post<{
        user: CurrentUserResponse;
        session_token: string;
        expires_at: string;
      }>("/auth/stop-impersonating"),
    onSuccess: ({ user }) => {
      qc.setQueryData(queryKeys.me, user);
      // Org-/user-scoped caches are stale (we just swapped identity).
      qc.invalidateQueries();
      // If the staff user was bounced to the contractor SPA during
      // impersonation, send them home now that they're staff again
      // — the contractor /jobs page has no org context for a
      // pencilout_admin so it would render empty.
      if (typeof window !== "undefined" && user.role === "pencilout_admin" && IS_CONTRACTOR_APP) {
        window.location.assign(STAFF_CONSOLE_ORIGIN);
      }
    },
  });
}

// ---- Dashboard ----

export function useDashboard(
  range: DashboardRangeKey = "this_month",
): UseQueryResult<DashboardMetrics, ApiError> {
  return useQuery<DashboardMetrics, ApiError>({
    queryKey: [...queryKeys.dashboard, range],
    queryFn: () => apiClient.get<DashboardMetrics>(`/dashboard?range=${range}`),
    staleTime: 30_000,
  });
}

// ---- Platform config (admin Settings page) ----

export function useAppConfig(): UseQueryResult<AppConfigResponse, ApiError> {
  return useQuery<AppConfigResponse, ApiError>({
    queryKey: queryKeys.appConfig,
    queryFn: () => apiClient.get<AppConfigResponse>("/admin/config"),
    staleTime: 30_000,
  });
}

export function useUpdateAppConfig() {
  const qc = useQueryClient();
  return useMutation<AppConfigResponse, ApiError, UpdateAppConfigRequest>({
    mutationFn: (body) => apiClient.patch<AppConfigResponse>("/admin/config", body),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.appConfig, data);
    },
  });
}

export function useModelCatalog(): UseQueryResult<ModelCatalogResponse, ApiError> {
  return useQuery<ModelCatalogResponse, ApiError>({
    queryKey: ["admin", "model-catalog"],
    queryFn: () => apiClient.get<ModelCatalogResponse>("/admin/config/model-catalog"),
    // Catalog only changes on a deploy (new pricing version), so
    // 5-minute cache is generous.
    staleTime: 5 * 60_000,
  });
}

// ---- Plan versions (customer-facing pricing tiers) ----

export function useCurrentPlanVersion(): UseQueryResult<PlanVersionResponse, ApiError> {
  return useQuery<PlanVersionResponse, ApiError>({
    queryKey: queryKeys.planVersionCurrent,
    queryFn: () => apiClient.get<PlanVersionResponse>("/admin/plans/current"),
    staleTime: 30_000,
  });
}

export function usePlanVersionHistory(): UseQueryResult<PlanVersionResponse[], ApiError> {
  return useQuery<PlanVersionResponse[], ApiError>({
    queryKey: queryKeys.planVersions,
    queryFn: () => apiClient.get<PlanVersionResponse[]>("/admin/plans/versions"),
    staleTime: 30_000,
  });
}

export function useSavePlanVersion() {
  const qc = useQueryClient();
  return useMutation<SaveNewPlanVersionResponse, ApiError, SaveNewPlanVersionRequest>({
    mutationFn: (body) => apiClient.post<SaveNewPlanVersionResponse>("/admin/plans/versions", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.planVersionCurrent });
      void qc.invalidateQueries({ queryKey: queryKeys.planVersions });
      void qc.invalidateQueries({ queryKey: queryKeys.publicPlans });
    },
  });
}

export function useSyncPlansToStripe() {
  const qc = useQueryClient();
  return useMutation<SyncStripeResponse, ApiError, void>({
    mutationFn: () => apiClient.post<SyncStripeResponse>("/admin/plans/sync-stripe"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.planVersionCurrent });
      void qc.invalidateQueries({ queryKey: queryKeys.planVersions });
    },
  });
}

/** Public plan list — no auth, used by the in-app signup form. The
 *  static marketing page (``coming-soon/pricing.html``) doesn't call
 *  this; it has its own hard-coded copy. */
export function usePublicPlans(): UseQueryResult<PublicPlanTierResponse[], ApiError> {
  return useQuery<PublicPlanTierResponse[], ApiError>({
    queryKey: queryKeys.publicPlans,
    queryFn: () => apiClient.get<PublicPlanTierResponse[]>("/plans/public"),
    staleTime: 5 * 60_000,
  });
}

// ---- Per-org billing actions (admin) ----

export function useEnsureOrgCustomer(orgId: string) {
  const qc = useQueryClient();
  return useMutation<{ customer_id: string; stripe_mode: string }, ApiError, void>({
    mutationFn: () =>
      apiClient.post<{ customer_id: string; stripe_mode: string }>(
        `/billing/orgs/${orgId}/customer`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.billingEventsForOrg(orgId),
      });
    },
  });
}

export function useGenerateOrgInvoice(orgId: string) {
  const qc = useQueryClient();
  return useMutation<GenerateInvoiceResponse, ApiError, { year?: number; month?: number }>({
    mutationFn: (body) => {
      const qs = new URLSearchParams();
      if (body.year !== undefined) qs.set("year", String(body.year));
      if (body.month !== undefined) qs.set("month", String(body.month));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return apiClient.post<GenerateInvoiceResponse>(`/billing/orgs/${orgId}/invoice${suffix}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.billingEventsForOrg(orgId),
      });
    },
  });
}

export function useOrgBillingEvents(orgId: string) {
  return useQuery<BillingEventResponse[], ApiError>({
    queryKey: queryKeys.billingEventsForOrg(orgId),
    queryFn: () => apiClient.get<BillingEventResponse[]>(`/billing/orgs/${orgId}/events`),
    staleTime: 30_000,
  });
}

// ---- Org subscription status + plan change (org-admin self-serve) ----

export function useSubscriptionStatus(orgId: string | null | undefined) {
  return useQuery<SubscriptionStatusResponse, ApiError>({
    queryKey: orgId
      ? queryKeys.subscriptionStatusForOrg(orgId)
      : ["billing", "subscription-status", "none"],
    queryFn: () =>
      apiClient.get<SubscriptionStatusResponse>(`/billing/orgs/${orgId}/subscription-status`),
    enabled: Boolean(orgId),
    staleTime: 15_000,
  });
}

/** Per-cycle overage breakdown. Org-admin gated server-side; the
 *  hook surfaces 403 as a normal error so the calling component
 *  can hide the UI. ``enabled`` lets PlanBillingPage skip the
 *  fetch entirely for callers we know aren't org_admin (no
 *  hammering an endpoint we know will 403). */
export function useOverageDetail(
  orgId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<OverageDetailResponse, ApiError>({
    queryKey: orgId ? queryKeys.overageDetailForOrg(orgId) : ["billing", "overage-detail", "none"],
    queryFn: () => apiClient.get<OverageDetailResponse>(`/billing/orgs/${orgId}/overage-detail`),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
    staleTime: 15_000,
  });
}

export function useCreateCheckoutSession(orgId: string) {
  return useMutation<CheckoutSessionResponse, ApiError, CheckoutSessionRequest>({
    mutationFn: (body) =>
      apiClient.post<CheckoutSessionResponse>(`/billing/orgs/${orgId}/checkout-session`, body),
  });
}

export function useRequestPlanChange(orgId: string) {
  const qc = useQueryClient();
  return useMutation<RequestPlanChangeResponse, ApiError, RequestPlanChangeBody>({
    mutationFn: (body) =>
      apiClient.post<RequestPlanChangeResponse>(`/billing/orgs/${orgId}/plan-change`, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.subscriptionStatusForOrg(orgId),
      });
    },
  });
}

export function useCancelPlanChange(orgId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await apiClient.delete(`/billing/orgs/${orgId}/plan-change`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.subscriptionStatusForOrg(orgId),
      });
    },
  });
}

// ---- Billing events (Stripe webhook viewer) ----

export interface BillingEventQueryParams {
  event_type?: string;
  succeeded?: boolean;
  limit?: number;
}

export function useAllBillingEvents(params: BillingEventQueryParams = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const path = `/admin/billing-events${qs.toString() ? `?${qs}` : ""}`;
  return useQuery<BillingEventResponse[], ApiError>({
    queryKey: ["admin", "billing-events", params] as const,
    queryFn: () => apiClient.get<BillingEventResponse[]>(path),
    staleTime: 5_000,
  });
}

// ---- Email events (Resend webhook viewer) ----

export interface EmailEventQueryParams {
  event_type?: string;
  recipient_email?: string;
  limit?: number;
}

export function useEmailEvents(params: EmailEventQueryParams = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const path = `/admin/email-events${qs.toString() ? `?${qs}` : ""}`;
  return useQuery<EmailEventResponse[], ApiError>({
    queryKey: ["admin", "email-events", params] as const,
    queryFn: () => apiClient.get<EmailEventResponse[]>(path),
    staleTime: 5_000,
  });
}

// ---- Admin audit log ----

/** Filter set for the audit-log viewer. Empty values omitted from
 *  the query string so the URL stays clean. */
export interface AuditEventQueryParams {
  action?: string;
  /** Prefix-match — ``organization.`` returns all org-touching
   *  actions. Mutually informative with ``action`` (which is
   *  exact-match); using both narrows further. */
  action_prefix?: string;
  target_id?: string;
  target_type?: string;
  actor_user_id?: string;
  /** Substring match, case-insensitive. */
  actor_email?: string;
  /** ISO-8601 lower bound (inclusive). */
  since?: string;
  /** ISO-8601 upper bound (exclusive). */
  until?: string;
  limit?: number;
}

export function useAuditEvents(params: AuditEventQueryParams = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const path = `/admin/audit-events${qs.toString() ? `?${qs}` : ""}`;
  return useQuery<AdminAuditEventResponse[], ApiError>({
    queryKey: ["admin", "audit-events", params] as const,
    queryFn: () => apiClient.get<AdminAuditEventResponse[]>(path),
    staleTime: 5_000,
  });
}

// ---- Service tokens (admin only) ----

export function useServiceTokens() {
  return useQuery<ServiceTokenResponse[], ApiError>({
    queryKey: queryKeys.serviceTokens,
    queryFn: () => apiClient.get<ServiceTokenResponse[]>("/admin/service-tokens"),
    staleTime: 15_000,
  });
}

export function useCreateServiceToken() {
  const qc = useQueryClient();
  return useMutation<CreateServiceTokenResponse, ApiError, CreateServiceTokenRequest>({
    mutationFn: (body) => apiClient.post<CreateServiceTokenResponse>("/admin/service-tokens", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.serviceTokens });
    },
  });
}

export function useRevokeServiceToken() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/admin/service-tokens/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.serviceTokens });
    },
  });
}

// ---- Pricing drift (admin only) ----

export function usePricingProposals(onlyPending = true) {
  return useQuery<PricingProposalResponse[], ApiError>({
    queryKey: queryKeys.pricingProposals(onlyPending),
    queryFn: () =>
      apiClient.get<PricingProposalResponse[]>(
        `/admin/pricing/proposals?only_pending=${onlyPending}`,
      ),
    staleTime: 30_000,
  });
}

export function useRunDriftCheck() {
  const qc = useQueryClient();
  return useMutation<PricingDriftCheckResponse, ApiError, void>({
    mutationFn: () => apiClient.post<PricingDriftCheckResponse>("/admin/pricing/drift-check"),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(true),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(false),
      });
    },
  });
}

export function useApplyPricingProposal() {
  const qc = useQueryClient();
  return useMutation<PricingVersionRecordResponse, ApiError, string>({
    mutationFn: (proposalId) =>
      apiClient.post<PricingVersionRecordResponse>(`/admin/pricing/proposals/${proposalId}/apply`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(true),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(false),
      });
    },
  });
}

export function useDismissPricingProposal() {
  const qc = useQueryClient();
  return useMutation<PricingProposalResponse, ApiError, { id: string; reason?: string | null }>({
    mutationFn: ({ id, reason }) =>
      apiClient.post<PricingProposalResponse>(`/admin/pricing/proposals/${id}/dismiss`, {
        reason: reason ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(true),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.pricingProposals(false),
      });
    },
  });
}

// ----------------------------------------------------------------
// Job templates (saved scopes)
// ----------------------------------------------------------------

/** GET /job-templates. Org users see their own org's templates;
 *  PencilOut staff see all unless they pass ``orgId``. */
export function useJobTemplates(orgId?: string): UseQueryResult<JobTemplateResponse[], ApiError> {
  return useQuery<JobTemplateResponse[], ApiError>({
    queryKey: orgId ? queryKeys.jobTemplatesForOrg(orgId) : queryKeys.jobTemplates,
    queryFn: () => {
      const qs = orgId ? `?organization_id=${orgId}` : "";
      return apiClient.get<JobTemplateResponse[]>(`/job-templates${qs}`);
    },
  });
}

export function useJobTemplate(
  id: string | undefined,
): UseQueryResult<JobTemplateResponse, ApiError> {
  return useQuery<JobTemplateResponse, ApiError>({
    queryKey: id ? queryKeys.jobTemplate(id) : ["job-templates", "disabled"],
    queryFn: () => apiClient.get<JobTemplateResponse>(`/job-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateJobTemplate(orgId?: string) {
  const qc = useQueryClient();
  return useMutation<JobTemplateResponse, ApiError, CreateJobTemplateRequest>({
    mutationFn: (body) => {
      const qs = orgId ? `?organization_id=${orgId}` : "";
      return apiClient.post<JobTemplateResponse>(`/job-templates${qs}`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobTemplates });
    },
  });
}

export function useUpdateJobTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation<JobTemplateResponse, ApiError, UpdateJobTemplateRequest>({
    mutationFn: (body) => apiClient.patch<JobTemplateResponse>(`/job-templates/${id}`, body),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.jobTemplate(id), fresh);
      void qc.invalidateQueries({ queryKey: queryKeys.jobTemplates });
    },
  });
}

export function useDeleteJobTemplate() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiClient.delete(`/job-templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobTemplates });
    },
  });
}

/** POST /quotes/{id}/apply-template — append (or replace) a
 *  template's line items onto an existing quote. Provenance
 *  protects AI + manual rows; only ``source=template`` rows are
 *  candidates for replacement when ``replace_existing=true``. */
export function useApplyTemplateToQuote(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<ApplyTemplateToQuoteResponse, ApiError, ApplyTemplateToQuoteRequest>({
    mutationFn: (body) =>
      apiClient.post<ApplyTemplateToQuoteResponse>(`/quotes/${quoteId}/apply-template`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quote(quoteId) });
      void qc.invalidateQueries({ queryKey: queryKeys.quotes });
    },
  });
}

// ----------------------------------------------------------------
// Webhook endpoints (outbound)
// ----------------------------------------------------------------

export function useWebhookEndpoints(): UseQueryResult<WebhookEndpointResponse[], ApiError> {
  return useQuery<WebhookEndpointResponse[], ApiError>({
    queryKey: queryKeys.webhookEndpoints,
    queryFn: () => apiClient.get<WebhookEndpointResponse[]>("/webhook-endpoints"),
  });
}

/** Returns the plaintext secret in the response — caller MUST surface
 *  it to the user immediately (with a copy button) since subsequent
 *  reads only get ``secret_set: true``. */
export function useCreateWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation<WebhookEndpointWithSecret, ApiError, CreateWebhookEndpointRequest>({
    mutationFn: (body) => apiClient.post<WebhookEndpointWithSecret>("/webhook-endpoints", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.webhookEndpoints });
    },
  });
}

export function useUpdateWebhookEndpoint(id: string) {
  const qc = useQueryClient();
  return useMutation<WebhookEndpointResponse, ApiError, UpdateWebhookEndpointRequest>({
    mutationFn: (body) =>
      apiClient.patch<WebhookEndpointResponse>(`/webhook-endpoints/${id}`, body),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.webhookEndpoint(id), fresh);
      void qc.invalidateQueries({ queryKey: queryKeys.webhookEndpoints });
    },
  });
}

export function useDeleteWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiClient.delete(`/webhook-endpoints/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.webhookEndpoints });
    },
  });
}

/** Mints a fresh signing secret + returns it once. Old secret is
 *  invalidated immediately. */
export function useRotateWebhookSecret() {
  const qc = useQueryClient();
  return useMutation<WebhookEndpointWithSecret, ApiError, string>({
    mutationFn: (id) =>
      apiClient.post<WebhookEndpointWithSecret>(`/webhook-endpoints/${id}/rotate-secret`),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.webhookEndpoint(fresh.id), fresh);
      void qc.invalidateQueries({ queryKey: queryKeys.webhookEndpoints });
    },
  });
}

export function useWebhookDeliveries(
  endpointId: string | null | undefined,
): UseQueryResult<WebhookDeliveryResponse[], ApiError> {
  return useQuery<WebhookDeliveryResponse[], ApiError>({
    queryKey: endpointId
      ? queryKeys.webhookDeliveries(endpointId)
      : ["webhook-endpoints", "deliveries", "disabled"],
    queryFn: () =>
      apiClient.get<WebhookDeliveryResponse[]>(`/webhook-endpoints/${endpointId}/deliveries`),
    enabled: Boolean(endpointId),
    refetchInterval: 15_000,
  });
}

/** POST /webhook-endpoints/{id}/deliveries/{did}/retry — re-fires
 *  the original payload with a fresh signature. Persists a new
 *  delivery row showing the retry's outcome; original row stays
 *  put as historical record. */
export function useRetryWebhookDelivery(endpointId: string) {
  const qc = useQueryClient();
  return useMutation<
    WebhookDeliveryResponse,
    ApiError,
    string // delivery id
  >({
    mutationFn: (deliveryID) =>
      apiClient.post<WebhookDeliveryResponse>(
        `/webhook-endpoints/${endpointId}/deliveries/${deliveryID}/retry`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.webhookDeliveries(endpointId),
      });
    },
  });
}

// ---- Change orders + project payments ----

/** GET /quotes/{id}/change-orders — the post-acceptance amendment
 *  ledger for a project (additional work, credits, refunds). */
export function useChangeOrders(
  quoteId: string | undefined,
): UseQueryResult<ChangeOrderResponse[], ApiError> {
  return useQuery<ChangeOrderResponse[], ApiError>({
    queryKey: quoteId ? queryKeys.changeOrders(quoteId) : ["change-orders", "disabled"],
    queryFn: () => apiClient.get<ChangeOrderResponse[]>(`/quotes/${quoteId}/change-orders`),
    enabled: Boolean(quoteId),
  });
}

export function useCreateChangeOrder(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<ChangeOrderResponse, ApiError, CreateChangeOrderRequest>({
    mutationFn: (body) =>
      apiClient.post<ChangeOrderResponse>(`/quotes/${quoteId}/change-orders`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.changeOrders(quoteId) });
    },
  });
}

export function useUpdateChangeOrder(quoteId: string, coId: string) {
  const qc = useQueryClient();
  return useMutation<ChangeOrderResponse, ApiError, UpdateChangeOrderRequest>({
    mutationFn: (body) => apiClient.patch<ChangeOrderResponse>(`/change-orders/${coId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.changeOrders(quoteId) });
    },
  });
}

export function useDeleteChangeOrder(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (coId) => apiClient.delete<void>(`/change-orders/${coId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.changeOrders(quoteId) });
    },
  });
}

/** GET /quotes/{id}/payments — the project's payment-receipt ledger
 *  (deposit + draws + final payment, etc.). Used by both the
 *  contractor's project view + the public homeowner view. */
export function usePayments(
  quoteId: string | undefined,
): UseQueryResult<ProjectPaymentResponse[], ApiError> {
  return useQuery<ProjectPaymentResponse[], ApiError>({
    queryKey: quoteId ? queryKeys.payments(quoteId) : ["payments", "disabled"],
    queryFn: () => apiClient.get<ProjectPaymentResponse[]>(`/quotes/${quoteId}/payments`),
    enabled: Boolean(quoteId),
  });
}

export function useCreatePayment(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectPaymentResponse, ApiError, CreatePaymentRequest>({
    mutationFn: (body) =>
      apiClient.post<ProjectPaymentResponse>(`/quotes/${quoteId}/payments`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.payments(quoteId) });
    },
  });
}

export function useUpdatePayment(quoteId: string, paymentId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectPaymentResponse, ApiError, UpdatePaymentRequest>({
    mutationFn: (body) => apiClient.patch<ProjectPaymentResponse>(`/payments/${paymentId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.payments(quoteId) });
    },
  });
}

export function useDeletePayment(quoteId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (paymentId) => apiClient.delete<void>(`/payments/${paymentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.payments(quoteId) });
    },
  });
}
